import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';

import { detectStaleJourneyState, DetectStaleJourneyStateResult } from './detectStaleJourneyState';

function StaleJourneyContainer({ children }: React.PropsWithChildren) {
  const location = useLocation();
  const navigate = useNavigate();

  const paymentSelect = useAppSelector(state => state.paymentSelect);
  const transaction = useAppSelector(state => state.transaction);

  const orderReference = paymentSelect.orderReference;
  const staleResultRef = useRef<(DetectStaleJourneyStateResult & {
    pathname: string,
    orderReference: string,
  }) | null>(null);

  // Re-run detection when the pathname or order reference changes.
  // Without pathname tracking, after a stale session redirect the StaleJourneyContainer
  // stops the homepage's child components from rendering.
  // The order reference detection can help in situations where isStale: true has
  // been cached before an order ref is created
  if (
    staleResultRef.current === null
    || staleResultRef.current.pathname !== location.pathname
    || staleResultRef.current.orderReference !== orderReference
  ) {
    const result = detectStaleJourneyState({
      pathname: location.pathname,
      paymentSelect,
      transaction,
    });

    staleResultRef.current = {
      pathname: location.pathname,
      orderReference,
      ...result,
    };
  }

  const isStale = staleResultRef.current.isStale;

  useEffect(() => {
    if (!isStale) {
      return;
    }

    logger.error(
      `Stale journey session detected at pathname=${location.pathname} reason=${staleResultRef.current?.reason ?? 'unknown'}; redirecting home.`
    );
    void navigate('/', { replace: true });
  }, [isStale, navigate, location.pathname]);

  if (isStale) {
    return null;
  }

  return children;
}

export default StaleJourneyContainer;
